import { motion } from "framer-motion";
import { Zap, LayoutGrid, TrendingUp, Globe, CreditCard, Video, Languages, Volume2 } from "lucide-react";
import { Badge } from "../ui/badge";

const ADD_ONS = [
  {
    icon: LayoutGrid,
    title: "Human-in-the-Loop",
    badge: "For cautious CEOs",
    badgeColor: "bg-orange-500/20 text-orange-400 border-orange-500/30",
    price: "$100",
    priceLabel: "/month",
    description: "Review Queue via Telegram. AI sends content for your approval before posting. Tap to Approve, Edit, or Reject.",
    note: "Requires free Telegram account",
    comingSoon: false
  },
  {
    icon: TrendingUp,
    title: "Autonomous Ad Management",
    badge: "Maximize ROI",
    badgeColor: "bg-primary/20 text-primary border-primary/30",
    price: "10%",
    priceLabel: "of ad spend",
    description: "AI monitors which post performs best and automatically puts $50+ ad spend behind winners.",
    note: null,
    comingSoon: false
  },
  {
    icon: Globe,
    title: "Multilingual SEO Blog",
    badge: "Content multiplier",
    badgeColor: "bg-green-500/20 text-green-400 border-green-500/30",
    price: "$200",
    priceLabel: "/month",
    description: "Agent converts TikTok scripts into 1,000-word SEO blog posts in 5 languages for your website.",
    note: null,
    comingSoon: false
  },
  {
    icon: Volume2,
    title: "AI Voice Responses",
    badge: "Included Free",
    badgeColor: "bg-primary/20 text-primary border-primary/30",
    price: "FREE",
    priceLabel: "",
    description: "YASNO speaks back to you! Get voice responses in 12 languages. Toggle on/off anytime in the chat.",
    note: "Works offline, no API needed",
    comingSoon: false
  }
];

const PAY_AS_YOU_GO = [
  {
    icon: Video,
    price: "$50",
    description: "5 additional high-quality AI videos"
  },
  {
    icon: Languages,
    price: "$20",
    priceLabel: "/month",
    description: "Specialized local dialect tuning"
  }
];

/**
 * AddOnsSection Component - Powerful Add-ons section
 */
export default function AddOnsSection() {
  return (
    <section className="py-24 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-12"
        >
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tighter mb-4 flex items-center justify-center gap-3">
            <Zap className="w-8 h-8 text-yellow-400" />
            <span>Powerful Add-ons</span>
          </h2>
          <p className="text-muted-foreground text-lg">
            Supercharge your marketing with these exclusive upgrades
          </p>
        </motion.div>

        {/* Add-on Cards */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
          {ADD_ONS.map((addon, index) => (
            <motion.div
              key={addon.title}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1 }}
              className={`glass-card rounded-2xl p-6 border transition-all ${
                addon.comingSoon 
                  ? 'border-yellow-500/30 hover:border-yellow-500/50 opacity-90' 
                  : 'border-white/10 hover:border-white/20'
              }`}
            >
              <div className="flex items-start justify-between mb-4">
                <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                  addon.comingSoon ? 'bg-yellow-500/10' : 'bg-primary/10'
                }`}>
                  <addon.icon className={`w-6 h-6 ${addon.comingSoon ? 'text-yellow-400' : 'text-primary'}`} />
                </div>
                <Badge className={addon.badgeColor}>
                  {addon.badge}
                </Badge>
              </div>
              
              <h3 className="text-xl font-bold mb-3">{addon.title}</h3>
              
              <div className="mb-4">
                <span className={`text-3xl font-bold ${addon.comingSoon ? 'text-yellow-400' : 'text-primary'}`}>
                  {addon.price}
                </span>
                <span className="text-muted-foreground text-sm">{addon.priceLabel}</span>
              </div>
              
              <p className="text-sm text-muted-foreground">
                {addon.description}
              </p>
              
              {addon.note && (
                <p className={`text-xs mt-3 flex items-center gap-1 ${
                  addon.comingSoon ? 'text-yellow-400/80' : 'text-orange-400/80'
                }`}>
                  <span className={`inline-block w-1 h-1 rounded-full ${
                    addon.comingSoon ? 'bg-yellow-400' : 'bg-orange-400'
                  }`}></span>
                  {addon.note}
                </p>
              )}
            </motion.div>
          ))}
        </div>

        {/* Pay-As-You-Go Credits */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center"
        >
          <h3 className="text-2xl font-bold mb-6 flex items-center justify-center gap-2">
            <CreditCard className="w-6 h-6 text-primary" />
            Pay-As-You-Go Credits
          </h3>
          
          <div className="flex flex-wrap justify-center gap-4">
            {PAY_AS_YOU_GO.map((item, index) => (
              <div
                key={index}
                className="flex items-center gap-3 px-6 py-4 rounded-full bg-white/5 border border-white/10"
              >
                <item.icon className="w-5 h-5 text-primary" />
                <span className="text-primary font-bold">{item.price}</span>
                {item.priceLabel && <span className="text-muted-foreground text-sm">{item.priceLabel}</span>}
                <span className="text-muted-foreground">— {item.description}</span>
              </div>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
}
